import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Gem, House, Search, ShieldCheck, Truck } from "lucide-react";
import MarketingShell from "../MarketingShell";
import SEOHead from "../../components/SEOHead";
import { useOpeningHours } from "../useBusinessInfo";
import useStoreProducts from "../store/useStoreProducts";
import { categoryGroup, jewelleryType, mainImage, opalType, pricing } from "../store/storeTaxonomy";
import { PriceLabel, STORE_PHONE, STORE_TEL, StoreMaintenance, productPath } from "../store/ShopParts";
import "./shop-landing.css";

/**
 * /online-store — the shop's front door (light-gallery redesign):
 * hero with search, trust strip, shop by category, shop by opal type,
 * souvenirs and a visit-the-showroom band. Sections and options with no live
 * products (e.g. rings, black opal) are left out, and appear by themselves
 * once such products are listed.
 */

const CATALOGUE = "/online-store/shop";

const TRUST = [
  [ShieldCheck, "Certificate of authenticity", "With every opal piece"],
  [Gem, "Mined in Coober Pedy", "The opal capital of the world"],
  [Truck, "Australia-wide shipping", "Dispatched from our showroom"],
  [House, "Family-owned", "Worked since the 1920s"],
];

const CATEGORY_TILES = [
  { name: "Rings", img: "/design/assets/prod-3.jpg", query: "type=Rings", test: (p) => jewelleryType(p) === "Rings" },
  { name: "Pendants", img: "/design/assets/tile-pendants.jpg", query: "type=Pendants", test: (p) => jewelleryType(p) === "Pendants" },
  { name: "Earrings", img: "/design/assets/prod-6.jpg", query: "type=Earrings", test: (p) => jewelleryType(p) === "Earrings" },
  // No stock photo for these yet: the tile uses the first product's photo
  ...["Bracelets", "Brooches", "Watches", "Cufflinks"].map((name) => ({
    name,
    img: null,
    query: `type=${name}`,
    test: (p) => jewelleryType(p) === name,
  })),
  {
    name: "Souvenirs & gifts",
    img: "/product-images/coober_pedy_coasters_2_86772dd2.jpg",
    query: "category=Souvenirs+%26+gifts",
    test: (p) => categoryGroup(p) === "Souvenirs & gifts",
  },
];

const OPAL_CARDS = [
  { type: "Solid", img: "/design/assets/opal-solid.png", diagram: true, tag: "Natural · investment",
    desc: "One piece of natural opal, cut and polished. Opal all the way through." },
  { type: "Black", img: "/design/assets/prod-11.jpg", diagram: false, tag: "Rarest",
    desc: "Solid opal with a dark body tone that makes the colour blaze. The rarest kind." },
  { type: "Boulder", img: "/product-images/boulder_opal_ring_435ct_1_1b2e267c.jpg", diagram: false, tag: "One of a kind",
    desc: "Opal formed inside ironstone, cut with its host rock left on the back." },
  { type: "Doublet", img: "/design/assets/opal-doublet.png", diagram: true, tag: "Great value",
    desc: "A thin slice of real opal on a dark backing that deepens its colour." },
  { type: "Triplet", img: "/design/assets/opal-triplet.png", diagram: true, tag: "Best for gifts",
    desc: "An opal layer under a clear quartz dome. Vivid, durable, affordable." },
  // No diagram for mosaic yet: the card uses a mosaic product's photo
  { type: "Mosaic", img: null, diagram: false, tag: "Statement colour",
    desc: "Small pieces of real opal set side by side like a mosaic, sealed under a clear cap. Bold colour, built for everyday wear." },
];

// Souvenirs first, then torches, then everything else (movies…); featured first within each
const souvenirRank = (p) => {
  const names = (p.categories || []).map((c) => c.name).join(" ");
  const group = /souvenir/i.test(names) ? 0 : /torch/i.test(names) ? 1 : 2;
  return group * 2 + (p.featured ? 0 : 1);
};

const NUMBER_WORDS = ["No", "One", "Two", "Three", "Four", "Five"];

const listPhrase = (words) =>
  words.length <= 1 ? words.join("") : `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;

export default function RShopLandingPage() {
  const navigate = useNavigate();
  const { products, loading, maintenance } = useStoreProducts();
  const hours = useOpeningHours();
  const [q, setQ] = useState("");
  const { search: queryString } = useLocation();

  // The old store page lived here with ?search= / ?category= filters; send
  // those links on to the catalogue with the same query.
  useEffect(() => {
    const old = new URLSearchParams(queryString);
    if (old.has("search") || old.has("category")) navigate(`${CATALOGUE}?${old.toString()}`, { replace: true });
  }, [queryString, navigate]);

  const data = useMemo(() => {
    const count = (test) => products.filter(test).length;
    const tiles = CATEGORY_TILES.map((t) => ({
      ...t,
      count: count(t.test),
      img: t.img || mainImage(products.find((p) => t.test(p) && mainImage(p)) || {}),
    })).filter((t) => (loading ? !!t.img : t.count > 0));
    const opalCards = OPAL_CARDS.map((o) => ({
      ...o,
      count: count((p) => opalType(p) === o.type),
      img: o.img || mainImage(products.find((p) => opalType(p) === o.type && mainImage(p)) || {}),
    })).filter((o) => (loading ? !!o.img : o.count > 0));
    const jewelleryNames = ["Rings", "Pendants", "Earrings"].filter((j) => loading || count((p) => jewelleryType(p) === j));
    const popular = [
      ["Black opal", "opal=Black", (p) => opalType(p) === "Black"],
      ["Rings", "type=Rings", (p) => jewelleryType(p) === "Rings"],
      ["Pendants", "type=Pendants", (p) => jewelleryType(p) === "Pendants"],
      ["Earrings", "type=Earrings", (p) => jewelleryType(p) === "Earrings"],
      ["Solid opal", "opal=Solid", (p) => opalType(p) === "Solid"],
      ["Under $200", "sort=low", (p) => pricing(p).price > 0 && pricing(p).price < 200],
    ]
      .filter(([, , test]) => !loading && count(test) > 0)
      .slice(0, 4);
    const souvenirs = products
      .filter((p) => categoryGroup(p) === "Souvenirs & gifts" && mainImage(p))
      .sort((a, b) => souvenirRank(a) - souvenirRank(b))
      .slice(0, 3);
    return { tiles, opalCards, jewelleryNames, popular, souvenirs };
  }, [products, loading]);

  const go = (query) => navigate(query ? `${CATALOGUE}?${query}` : CATALOGUE);

  const submitSearch = (e) => {
    e.preventDefault();
    const term = q.trim();
    go(term ? `search=${encodeURIComponent(term)}` : "");
  };

  const jewellery = data.jewelleryNames.map((j) => j.toLowerCase());
  const heroLead = `${listPhrase(jewellery.length ? jewellery : ["pieces"]).replace(/^./, (c) => c.toUpperCase())} set with Australian opal — every piece sold with a certificate of authenticity and shipped Australia-wide.`;
  const placeholder = `Search ${[...jewellery.slice(0, 2), "solid opal"].join(", ")}…`;
  const typeCount = data.opalCards.length;
  const typeIntro =
    typeCount >= 2
      ? `${NUMBER_WORDS[typeCount] || typeCount} kinds of opal, ${(NUMBER_WORDS[typeCount] || String(typeCount)).toLowerCase()} price points. Here's the short version of what makes each one different.`
      : "Here's the short version of what makes our opal special.";
  const hoursText = (hours.summary || [hours.hours]).join("; ").replace(/^./, (c) => c.toLowerCase());

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Store",
    name: "Umoona Opal Mine Online Store",
    description: "Shop authentic Australian opal jewellery and Coober Pedy souvenirs online.",
    url: "https://umoonaopalmine.com.au/online-store",
  };

  return (
    <MarketingShell pageClass="pg-shop pg-shop-landing">
      <SEOHead
        title="Shop Australian Opal Jewellery Online | Umoona Opal Mine Store"
        description="Genuine Australian opal pendants and earrings from Coober Pedy, each with a certificate of authenticity and shipped Australia-wide. Souvenirs and gifts too."
        canonical="/online-store"
        schemaData={schemaData}
      />

      {maintenance ? (
        <StoreMaintenance />
      ) : (
        <>
          {/* ============ HERO ============ */}
          <section className="sl-hero">
            <div className="sh-wrap sl-hero__grid">
              <div>
                <div className="sh-eyebrow sl-hero__eyebrow">The Umoona Opal Store · Coober Pedy</div>
                <h1 className="sl-hero__title">Genuine opal, straight from the opal capital.</h1>
                <p className="sl-hero__lead">{heroLead}</p>
                <form className="sl-search" onSubmit={submitSearch} role="search">
                  <span className="sl-search__icon">
                    <Search size={18} />
                  </span>
                  <input
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={placeholder}
                    aria-label="Search the opal store"
                  />
                  <button type="submit">Search</button>
                </form>
                {data.popular.length > 0 && (
                  <div className="sl-popular">
                    <span className="sl-popular__label">Popular</span>
                    {data.popular.map(([label, query]) => (
                      <button key={label} type="button" className="sl-pill" onClick={() => go(query)}>
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="sl-hero__media">
                <img src="/design/assets/photo-opal.jpg" alt="Opal pendant on Coober Pedy rock" />
              </div>
            </div>
          </section>

          {/* ============ TRUST STRIP ============ */}
          <section className="sl-trust">
            <div className="sh-wrap sl-trust__grid">
              {TRUST.map(([Icon, title, sub]) => (
                <div key={title} className="sl-trust__item">
                  <Icon size={22} className="sl-trust__icon" />
                  <div>
                    <div className="sl-trust__title">{title}</div>
                    <div className="sl-trust__sub">{sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ============ SHOP BY CATEGORY ============ */}
          <section className="sl-sect">
            <div className="sh-wrap">
              <div className="sl-sect__head">
                <h2 className="sh-h2">Shop by category</h2>
                <Link to={CATALOGUE} className="sl-viewall">
                  View all {loading ? "" : `${products.length} `}pieces <ArrowRight size={16} />
                </Link>
              </div>
              <div className="sl-cats" style={{ "--cols": Math.min(Math.max(data.tiles.length, 1), 5) }}>
                {data.tiles.map((t) => (
                  <Link key={t.name} to={`${CATALOGUE}?${t.query}`} className="sl-cat">
                    <div className="sl-cat__media">
                      <img src={t.img} alt={t.name} loading="lazy" decoding="async" />
                    </div>
                    <div className="sl-cat__row">
                      <span className="sl-cat__name">{t.name}</span>
                      {!loading && <span className="sl-cat__count">{t.count} {t.count === 1 ? "piece" : "pieces"}</span>}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* ============ SHOP BY OPAL TYPE ============ */}
          {data.opalCards.length > 0 && (
            <section className="sl-sect sl-sect--sand">
              <div className="sh-wrap">
                <div className="sl-types__head">
                  <div className="sh-eyebrow">Know your opal</div>
                  <h2 className="sh-h2">Shop by opal type</h2>
                  <p>{typeIntro}</p>
                </div>
                <div className="sl-types" style={{ "--cols": Math.max(data.opalCards.length, 1) }}>
                  {data.opalCards.map((o) => (
                    <Link key={o.type} to={`${CATALOGUE}?opal=${o.type}`} className="sl-type">
                      <div className="sl-type__media">
                        <img
                          src={o.img}
                          alt={`${o.type} opal`}
                          className={o.diagram ? "is-diagram" : ""}
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                      <div className="sl-type__body">
                        <div className="sl-type__row">
                          <span className="sl-type__name">{o.type} opal</span>
                          {!loading && <span className="sl-type__count">{o.count}</span>}
                        </div>
                        <p className="sl-type__desc">{o.desc}</p>
                        <span className="sl-type__tag">{o.tag}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* ============ SOUVENIRS ============ */}
          {(loading || data.souvenirs.length > 0) && (
            <section className="sl-sect">
              <div className="sh-wrap sl-souv">
                <div>
                  <div className="sh-eyebrow">Souvenirs &amp; merch</div>
                  <h2 className="sh-h2 sl-souv__title">Take a piece of Coober Pedy home</h2>
                  <p className="sl-souv__copy">
                    Black light torches for spotting opal, coasters, stickers and outback metal art — easy gifts
                    from the underground town.
                  </p>
                  <Link to={`${CATALOGUE}?category=Souvenirs+%26+gifts`} className="sh-btn sh-btn--ink">
                    Shop souvenirs
                  </Link>
                </div>
                <div className="sl-souv__grid">
                  {data.souvenirs.map((p) => (
                    <Link key={p._id || p.id} to={productPath(p)} className="sl-souv__item">
                      <div className="sl-souv__media">
                        <img src={mainImage(p)} alt={p.name} loading="lazy" decoding="async" />
                      </div>
                      <div className="sl-souv__name">{p.name}</div>
                      <div className="sl-souv__price">
                        <PriceLabel product={p} />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* ============ VISIT BAND ============ */}
          <section className="sl-visitwrap">
            <div className="sh-wrap sl-visit">
              <div className="sl-visit__media">
                <img src="/design/assets/photo-showroom.jpg" alt="The Umoona opal showroom" loading="lazy" decoding="async" />
              </div>
              <div className="sl-visit__body">
                <h2 className="sl-visit__title">Prefer to choose in person?</h2>
                <p className="sl-visit__copy">
                  Visit the showroom at 14 Hutchison St, Coober Pedy — {hoursText}. Our team will help you compare
                  stones side by side.
                </p>
                <div className="sl-visit__btns">
                  <Link to="/opal-store" className="sh-btn sh-btn--accent">
                    About the Store
                  </Link>
                  <a href={STORE_TEL} className="sh-btn sl-visit__ghost">
                    Call {STORE_PHONE}
                  </a>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
    </MarketingShell>
  );
}
